import React from "react";

const styles = `
  @keyframes dlt-green-spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  .dlt-green-spinner {
    animation: dlt-green-spin 1.8s linear infinite;
    transform-origin: 60px 60px;
  }
  @media (prefers-reduced-motion: reduce) {
    .dlt-green-spinner {
      animation: none;
    }
  }
`;

/**
 * DeloitteLoader
 *
 * A circular arc loader in Deloitte brand green.
 * Starts vivid green (#86EB22) at the leading edge and fades
 * to a very light pale green at the trailing end.
 * Center is fully transparent.
 *
 * Props:
 *   size      {number}  — width & height in px   (default: 80)
 *   speed     {string}  — animation duration      (default: "1.8s")
 *   className {string}  — extra class on <svg>
 *   style     {object}  — extra inline styles on <svg>
 *
 * Usage:
 *   import DeloitteLoader from "./DeloitteLoader";
 *
 *   <DeloitteLoader />
 *   <DeloitteLoader size={120} speed="1.2s" />
 *   <DeloitteLoader size={40} style={{ display: "block", margin: "auto" }} />
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export default function DeloitteLoader({
  size = 80,
  speed = "1.8s",
  className = "",
  style = {},
}: {
  size?: number;
  speed?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const r = 47;
  const circ = 2 * Math.PI * r; // ≈ 295.3
  const arcLen = (circ * 320) / 360; // ~320deg visible arc

  const spinStyle = {
    animation: `dlt-green-spin ${speed} linear infinite`,
    transformOrigin: "60px 60px",
  };

  return (
    <>
      <style>{styles}</style>
      <svg
        width={size}
        height={size}
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Loading…"
        className={className}
        style={style}
      >
        <title>Loading</title>
        <desc>
          Circular arc loader fading from vivid Deloitte green at the leading
          edge to pale green at the trailing end, with a transparent center.
        </desc>

        <defs>
          <linearGradient id="dlt-green-grad" x1="1" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#86EB22" stopOpacity="1" />
            <stop offset="30%"  stopColor="#86BC25" stopOpacity="1" />
            <stop offset="65%"  stopColor="#b8dd80" stopOpacity="1" />
            <stop offset="100%" stopColor="#dff2b8" stopOpacity="0.35" />
          </linearGradient>
        </defs>

        <g style={spinStyle}>
          <circle
            cx="60" cy="60" r={r}
            fill="none"
            stroke="url(#dlt-green-grad)"
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={`${arcLen.toFixed(1)} ${circ.toFixed(1)}`}
            strokeDashoffset="0"
            transform="rotate(-90 60 60)"
          />
        </g>
      </svg>
    </>
  );
}
